// ════════════════════════════════════════════════════════════════════════════
// HOTELS — the pillar's RESULT SHAPE, and the one reader/writer pair both
// sides use.
//
// This is the artifact the map has and the attach grammar did not: a TYPED
// per-pillar object (`ParsedMap`'s `MapPin`, one pillar down), plus the single
// shared function that produces it from a block and the single shared function
// that writes a block from it. The BL writes THROUGH `writeHotelsAttachItem`;
// the FE reads THROUGH `readHotelsAttachItem`. Two implementations of one
// grammar is the drift `Cmarkdownmap`'s own header exists to forbid — "the
// same one the back end reads, so the two can never disagree".
//
// ─── THE HEADLINE CARRIES NO DATA ───────────────────────────────────────────
//
// It used to. `TSUKIMI HOTEL | hotel | ⭐⭐⭐ | 📍Gion, JP (35.0009321,
// 135.7784988) | 4.8/5 498 reviews` folded SIX data points into one prose
// string, and a widget could not get a rating, a star class or a coordinate
// out of it without regexing prose per pillar. Ruled 2026-08-20: every datum
// is its own addressable field, and the headline is COMPOSED FROM those fields
// as a display line. It is therefore not a restatement — it is a projection of
// the record, so there is exactly one source of truth — and `readHotelsAttachItem`
// never looks at it. A reader may delete the headline from a stored block and
// lose nothing but the human read.
//
// ─── WHAT THE FIELDS ARE, AND WHERE THEY CAME FROM ──────────────────────────
//
// Authored from what the pillar's own projection PROVES it holds
// (`SearchApiGoogleHotelsPropertyProjection`, list and property-drill), whose
// census is pinned in `hotels.attach.ts`: 190 list rows over ten answering
// captures, 3 drill captures. Nothing here is a field nobody has observed.
//
// Three vendor values are DISPLAY STRINGS on the wire and are typed as strings
// on purpose — `price_insights.lowest_price` is `"$201"`, not 201, and
// `typical_price_range` is a pair of the same. A number here would be OURS,
// not the vendor's, which is the invented-field class the census names.
// `check_in_time` / `check_out_time` are localized clock strings carrying a
// U+202F narrow no-break space, carried verbatim for the same reason. So is an
// offer's FREE-CANCELLATION DEADLINE (`Sep 18`, `11:59 PM`): the vendor sends
// no year and no timezone, so an instant here would be ours, and the two halves
// stay two members because the vendor sends two and the glyph that joins them
// for a reader is the drawing surface's choice, never the block's.
//
// SOLD OUT IS THE ABSENCE OF THE THREE PRICE FIELDS, not a sentence. The row
// that used to print `no availability for this window` now prints no price
// field at all, and the sentence is the FE's to compose from
// `pricePerNightUsd === null` — absent and zero stay different answers, and a
// widget can now tell them apart without reading English.
// ════════════════════════════════════════════════════════════════════════════

import type { AttachItemBlock, AttachFieldLine } from '../grammar.js';
import { attachFieldValue, attachFieldValues } from '../parse-attach-document.js';
import {
  attachCoordinatesOf,
  attachCoordinatesText,
  attachFieldGroups,
  attachFlagText,
  attachGroupValue,
  attachGroupValues,
  attachNumberOf,
  attachNumberText,
  attachPartFlag,
  attachPartNumber,
  attachPartText,
  attachSubRecordOf,
  attachSubRecordText,
  attachTextOf,
  type AttachCoordinates,
} from './attach-value-codec.js';

/**
 * The tagged keys a hotels item prints. Closed set, named for what the reader
 * is looking at rather than for what we call the field (D19).
 *
 * It lives HERE and not beside the BL projector because both sides need it: a
 * key is written by one and read by the other, and one spelling in one home is
 * the rule the fence, the separators and the pillar names are already kept
 * under.
 */
export enum HotelsAttachField {
  Name = 'name',
  /** Hotel or vacation rental — a hotel-mode search returns both, and which it
   *  is decides whether a party of four fits and whether anyone is at a desk
   *  at midnight. */
  PropertyKind = 'property kind',
  /** Numeric star class, 1-5. Hotels only; a rental carries `essential`. */
  Stars = 'stars',
  /** Google's 0-5 score. */
  Rating = 'rating',
  /** How many reviews that score is over — 4.75 on 8 is not 4.7 on 14,896. */
  Reviews = 'reviews',
  /** How many nights every price below is for. On the ITEM and not only on the
   *  block's framing, because it is what makes the three price figures
   *  interpretable at all and a typed item has to be readable on its own. */
  Nights = 'nights',
  /** The currency the three price figures are denominated in. */
  Currency = 'currency',
  /** Tax-inclusive nightly. */
  PricePerNight = 'price per night',
  /** Pre-tax nightly — the vendor sends BOTH and names neither, and one
   *  unlabelled nightly figure is what once sent a writer mining a tax number
   *  out of a booking URL's query string. */
  PricePerNightPretax = 'price per night pretax',
  /** Whole-stay total for the window. NOT the nightly times the nights: the
   *  vendor rounds each display value independently. */
  TotalStay = 'total stay',
  City = 'city',
  /** ISO-2 code. */
  Country = 'country',
  /** `lat, lon`, the map's own literal-coordinate form. */
  Coordinates = 'coordinates',
  /** The street address. DRILL ONLY — the list response carries none. */
  Address = 'address',
  /** DRILL ONLY. */
  Phone = 'phone',
  LocationRating = 'location rating',
  ThingsToDoRating = 'things to do rating',
  TransitRating = 'transit rating',
  AirportRating = 'airport rating',
  /** One measured door-to-door time. REPEATED — one line per (place, mode)
   *  pair, because `8 min` on foot and `8 min` by taxi are different answers
   *  and the vendor sends rows with no mode at all. */
  Nearby = 'nearby',
  CheckIn = 'check in',
  CheckOut = 'check out',
  /** REPEATED — one amenity per line. A repeated key is the grammar's ordered
   *  sub-array form and needs no separator, so an amenity carrying any
   *  character at all still round-trips. */
  Amenity = 'amenity',
  /** REPEATED. The rental's counterpart of a star class. */
  Essential = 'essential',
  /** REPEATED — what guests kept bringing up, with the counts. */
  ReviewAspect = 'review aspect',
  /** The one OTA quote a LIST row can carry. Rentals only in every capture. */
  BestOffer = 'best offer',
  /** Google's floor for this property, as the vendor's own display string. */
  PriceLowest = 'price lowest',
  /** `typical` / `high` — the vendor's verdict on the figure. */
  PriceLevel = 'price level',
  PriceTypicalLow = 'price typical low',
  PriceTypicalHigh = 'price typical high',
  /** One bookable quote. DRILL ONLY, REPEATED, in the vendor's featured order.
   *  Opens a GROUP: the `offer room` and `book` lines under it are its own. */
  Offer = 'offer',
  /** A room name on the offer above. REPEATED inside its offer. Free text —
   *  room names carry commas — so it takes its own line and never folds into
   *  the offer's sub-record. */
  OfferRoom = 'offer room',
  /** The URL that books the offer above it. */
  Book = 'book',
  /** One guest, by name. DRILL ONLY, REPEATED. Opens a GROUP whose `guest said`
   *  line carries the words. */
  Guest = 'guest',
  /** What that guest wrote, verbatim. Free text, its own line (D21). */
  GuestSaid = 'guest said',
  /** The property's score on another review site. DRILL ONLY, REPEATED. */
  OtherSite = 'other site',
  /** The property's photo — a data link, labeled by what it represents (R17). */
  Photo = 'photo',
  /** The property's own page — the row's one DURABLE address (U3, SPEC §5b). */
  PropertyPage = 'property page',
}

/** The parts of the sub-records this pillar writes. Same rule as the field
 *  keys: one spelling, one home, both directions. */
export enum HotelsAttachPart {
  Place = 'place',
  Mode = 'mode',
  Duration = 'duration',
  Aspect = 'aspect',
  Total = 'total',
  Positive = 'positive',
  Negative = 'negative',
  Seller = 'seller',
  PricePerNight = 'price per night',
  PricePerNightPretax = 'pretax',
  TotalStay = 'total stay',
  FreeCancellation = 'free cancellation',
  /** The two halves of the deadline the flag above runs to. Named for the
   *  reader's own question — by WHEN — rather than after the wire's
   *  `free_cancellation_until`, and kept as two parts because the vendor sends
   *  two and either can arrive without the other. Both are CONSTRAINED display
   *  atoms (`Sep 18`, `11:59 PM`), so they fold into the offer's sub-record;
   *  free text, which a room name is, would not (D21). */
  CancelByDate = 'cancel by date',
  CancelByTime = 'cancel by time',
  Name = 'name',
  Site = 'site',
  Rating = 'rating',
  Reviews = 'reviews',
}

/** What kind of place a row IS. The vendor's own two-member enum, in the words
 *  a reader uses rather than the wire's snake_case, and read back by exact
 *  match against this table — never guessed at. */
export enum HotelsAttachPropertyKind {
  Hotel = 'hotel',
  VacationRental = 'vacation rental',
}

const HOTELS_PROPERTY_KINDS: ReadonlySet<string> = new Set<string>(
  Object.values(HotelsAttachPropertyKind),
);

// ─── the typed shape ────────────────────────────────────────────────────────

/** One measured door-to-door time from the property. */
export interface HotelsAttachNearby {
  readonly place: string;
  /** Walking / Taxi / Public Transport, localized by the vendor. Null when the
   *  vendor sent a duration with no mode, which it does. */
  readonly mode: string | null;
  /** The vendor's display string (`11 min`, `2 hr 9 min`) — never numeric on
   *  the wire, so never numeric here. */
  readonly duration: string | null;
}

export interface HotelsAttachReviewAspect {
  readonly aspect: string;
  readonly total: number | null;
  readonly positive: number | null;
  readonly negative: number | null;
}

export interface HotelsAttachBestOffer {
  readonly seller: string;
  readonly pricePerNightPretaxUsd: number | null;
  readonly freeCancellation: boolean | null;
}

export interface HotelsAttachOffer {
  readonly seller: string | null;
  readonly pricePerNightUsd: number | null;
  readonly pricePerNightPretaxUsd: number | null;
  readonly totalStayUsd: number | null;
  readonly freeCancellation: boolean | null;
  /** How long the flag above holds, as the vendor's own two display strings —
   *  `Sep 18` and `11:59 PM`, carried VERBATIM and never parsed to an instant.
   *  There is no year and no timezone on the wire, so a date here would be a
   *  value we invented; the clock half carries the same U+202F narrow no-break
   *  space `checkInTime` does. TWO members and not one joined line: a join is a
   *  READING, and the surface that draws the row picks its own separator. */
  readonly freeCancellationUntilDate: string | null;
  readonly freeCancellationUntilTime: string | null;
  readonly rooms: readonly string[];
  readonly bookUrl: string | null;
}

export interface HotelsAttachGuestQuote {
  readonly name: string | null;
  readonly text: string | null;
}

export interface HotelsAttachOtherSiteRating {
  readonly site: string;
  readonly rating: number | null;
  readonly reviewCount: number | null;
}

/** Google's verdict on the figure — every member a display string, because
 *  every one of them is one on the wire. */
export interface HotelsAttachPriceCheck {
  readonly lowestText: string | null;
  readonly level: string | null;
  readonly typicalLowText: string | null;
  readonly typicalHighText: string | null;
}

/** One property, typed. The object a widget renders from. */
export interface HotelsAttachResult {
  readonly name: string;
  readonly propertyKind: HotelsAttachPropertyKind | null;
  readonly stars: number | null;
  readonly rating: number | null;
  readonly reviewCount: number | null;
  readonly nights: number | null;
  readonly currency: string | null;
  readonly pricePerNightUsd: number | null;
  readonly pricePerNightPretaxUsd: number | null;
  readonly totalStayUsd: number | null;
  readonly city: string | null;
  readonly country: string | null;
  readonly coordinates: AttachCoordinates | null;
  readonly address: string | null;
  readonly phone: string | null;
  readonly locationRating: number | null;
  readonly thingsToDoRating: number | null;
  readonly transitRating: number | null;
  readonly airportRating: number | null;
  readonly nearby: readonly HotelsAttachNearby[];
  readonly checkInTime: string | null;
  readonly checkOutTime: string | null;
  readonly amenities: readonly string[];
  readonly essentials: readonly string[];
  readonly reviewAspects: readonly HotelsAttachReviewAspect[];
  readonly bestOffer: HotelsAttachBestOffer | null;
  readonly priceCheck: HotelsAttachPriceCheck | null;
  readonly offers: readonly HotelsAttachOffer[];
  readonly guestQuotes: readonly HotelsAttachGuestQuote[];
  readonly otherSiteRatings: readonly HotelsAttachOtherSiteRating[];
  readonly photoUrl: string | null;
  readonly propertyPageUrl: string | null;
}

// ─── the headline, composed FROM the fields ─────────────────────────────────

const HEADLINE_SEPARATOR = ' | ';
const STAR_GLYPH = '⭐';
const RATING_SCALE = '/5';
const PLACE_SEPARATOR = ', ';
const REVIEWS_OPEN = ' (';
const REVIEWS_CLOSE = ' reviews)';

function starsText(input: { stars: number | null }): string | null {
  const stars = input.stars;
  if (stars === null || !Number.isInteger(stars) || stars <= 0) return null;
  return STAR_GLYPH.repeat(stars);
}

function placeText(input: { city: string | null; country: string | null }): string | null {
  const parts = [input.city, input.country].filter(
    (part): part is string => part !== null && part.length > 0,
  );
  return parts.length === 0 ? null : parts.join(PLACE_SEPARATOR);
}

function scoreText(input: { rating: number | null; reviewCount: number | null }): string | null {
  if (input.rating === null) return null;
  const score = `${String(input.rating)}${RATING_SCALE}`;
  return input.reviewCount === null
    ? score
    : `${score}${REVIEWS_OPEN}${String(input.reviewCount)}${REVIEWS_CLOSE}`;
}

/**
 * The human read of one property — DISPLAY ONLY.
 *
 * Every value in it is read straight off the typed object, so the line cannot
 * state anything the fields do not. It is here rather than in the FE because a
 * stored block must carry a line a PERSON reading the version body can scan,
 * and that line has to be written at render time; the FE composes its own from
 * the same object and never parses this one.
 */
export function hotelsAttachHeadline(input: { result: HotelsAttachResult }): string {
  const result = input.result;
  return [
    result.name,
    result.propertyKind,
    starsText({ stars: result.stars }),
    placeText({ city: result.city, country: result.country }),
    scoreText({ rating: result.rating, reviewCount: result.reviewCount }),
  ]
    .filter((part): part is string => part !== null && part.length > 0)
    .join(HEADLINE_SEPARATOR);
}

// ─── writing ────────────────────────────────────────────────────────────────

function textLine(input: {
  key: HotelsAttachField;
  value: string | null;
}): AttachFieldLine[] {
  const value = attachTextOf({ value: input.value });
  return value === null ? [] : [{ key: input.key, value }];
}

function numberLine(input: {
  key: HotelsAttachField;
  value: number | null;
}): AttachFieldLine[] {
  if (input.value === null) return [];
  const value = attachNumberText({ value: input.value });
  return value === null ? [] : [{ key: input.key, value }];
}

function subRecordLine(input: {
  key: HotelsAttachField;
  parts: readonly { key: HotelsAttachPart; value: string | null }[];
}): AttachFieldLine[] {
  const value = attachSubRecordText({ parts: input.parts });
  return value === null ? [] : [{ key: input.key, value }];
}

function numberPart(input: { value: number | null }): string | null {
  return input.value === null ? null : attachNumberText({ value: input.value });
}

function flagPart(input: { value: boolean | null }): string | null {
  return input.value === null ? null : attachFlagText({ value: input.value });
}

function offerLines(input: { offer: HotelsAttachOffer }): AttachFieldLine[] {
  const offer = input.offer;
  return [
    ...subRecordLine({
      key: HotelsAttachField.Offer,
      parts: [
        { key: HotelsAttachPart.Seller, value: offer.seller },
        {
          key: HotelsAttachPart.PricePerNight,
          value: numberPart({ value: offer.pricePerNightUsd }),
        },
        {
          key: HotelsAttachPart.PricePerNightPretax,
          value: numberPart({ value: offer.pricePerNightPretaxUsd }),
        },
        { key: HotelsAttachPart.TotalStay, value: numberPart({ value: offer.totalStayUsd }) },
        {
          key: HotelsAttachPart.FreeCancellation,
          value: flagPart({ value: offer.freeCancellation }),
        },
        { key: HotelsAttachPart.CancelByDate, value: offer.freeCancellationUntilDate },
        { key: HotelsAttachPart.CancelByTime, value: offer.freeCancellationUntilTime },
      ],
    }),
    ...offer.rooms.flatMap((room) =>
      textLine({ key: HotelsAttachField.OfferRoom, value: room })),
    ...textLine({ key: HotelsAttachField.Book, value: offer.bookUrl }),
  ];
}

/**
 * One typed property as a block item.
 *
 * Field order is the reading order the pillar's own answer keeps — what it is,
 * what it costs, where it is, how it scores, what it has, what guests said,
 * then the bookable quotes and the links. The ORDER of a repeated key is the
 * sub-array it encodes and is preserved exactly.
 */
export function writeHotelsAttachItem(input: {
  result: HotelsAttachResult;
}): AttachItemBlock {
  const result = input.result;
  const priceCheck = result.priceCheck;
  const fields: AttachFieldLine[] = [
    ...textLine({ key: HotelsAttachField.Name, value: result.name }),
    ...textLine({ key: HotelsAttachField.PropertyKind, value: result.propertyKind }),
    ...numberLine({ key: HotelsAttachField.Stars, value: result.stars }),
    ...numberLine({ key: HotelsAttachField.Rating, value: result.rating }),
    ...numberLine({ key: HotelsAttachField.Reviews, value: result.reviewCount }),
    ...numberLine({ key: HotelsAttachField.Nights, value: result.nights }),
    ...textLine({ key: HotelsAttachField.Currency, value: result.currency }),
    ...numberLine({ key: HotelsAttachField.PricePerNight, value: result.pricePerNightUsd }),
    ...numberLine({
      key: HotelsAttachField.PricePerNightPretax,
      value: result.pricePerNightPretaxUsd,
    }),
    ...numberLine({ key: HotelsAttachField.TotalStay, value: result.totalStayUsd }),
    ...textLine({ key: HotelsAttachField.City, value: result.city }),
    ...textLine({ key: HotelsAttachField.Country, value: result.country }),
    ...textLine({
      key: HotelsAttachField.Coordinates,
      value: result.coordinates === null
        ? null
        : attachCoordinatesText({ value: result.coordinates }),
    }),
    ...textLine({ key: HotelsAttachField.Address, value: result.address }),
    ...textLine({ key: HotelsAttachField.Phone, value: result.phone }),
    ...numberLine({ key: HotelsAttachField.LocationRating, value: result.locationRating }),
    ...numberLine({ key: HotelsAttachField.ThingsToDoRating, value: result.thingsToDoRating }),
    ...numberLine({ key: HotelsAttachField.TransitRating, value: result.transitRating }),
    ...numberLine({ key: HotelsAttachField.AirportRating, value: result.airportRating }),
    ...result.nearby.flatMap((nearby) =>
      subRecordLine({
        key: HotelsAttachField.Nearby,
        parts: [
          { key: HotelsAttachPart.Place, value: nearby.place },
          { key: HotelsAttachPart.Mode, value: nearby.mode },
          { key: HotelsAttachPart.Duration, value: nearby.duration },
        ],
      })),
    ...textLine({ key: HotelsAttachField.CheckIn, value: result.checkInTime }),
    ...textLine({ key: HotelsAttachField.CheckOut, value: result.checkOutTime }),
    ...result.amenities.flatMap((amenity) =>
      textLine({ key: HotelsAttachField.Amenity, value: amenity })),
    ...result.essentials.flatMap((essential) =>
      textLine({ key: HotelsAttachField.Essential, value: essential })),
    ...result.reviewAspects.flatMap((aspect) =>
      subRecordLine({
        key: HotelsAttachField.ReviewAspect,
        parts: [
          { key: HotelsAttachPart.Aspect, value: aspect.aspect },
          { key: HotelsAttachPart.Total, value: numberPart({ value: aspect.total }) },
          { key: HotelsAttachPart.Positive, value: numberPart({ value: aspect.positive }) },
          { key: HotelsAttachPart.Negative, value: numberPart({ value: aspect.negative }) },
        ],
      })),
    ...(result.bestOffer === null
      ? []
      : subRecordLine({
          key: HotelsAttachField.BestOffer,
          parts: [
            { key: HotelsAttachPart.Seller, value: result.bestOffer.seller },
            {
              key: HotelsAttachPart.PricePerNightPretax,
              value: numberPart({ value: result.bestOffer.pricePerNightPretaxUsd }),
            },
            {
              key: HotelsAttachPart.FreeCancellation,
              value: flagPart({ value: result.bestOffer.freeCancellation }),
            },
          ],
        })),
    ...(priceCheck === null
      ? []
      : [
          ...textLine({ key: HotelsAttachField.PriceLowest, value: priceCheck.lowestText }),
          ...textLine({ key: HotelsAttachField.PriceLevel, value: priceCheck.level }),
          ...textLine({
            key: HotelsAttachField.PriceTypicalLow,
            value: priceCheck.typicalLowText,
          }),
          ...textLine({
            key: HotelsAttachField.PriceTypicalHigh,
            value: priceCheck.typicalHighText,
          }),
        ]),
    ...result.offers.flatMap((offer) => offerLines({ offer })),
    ...result.guestQuotes.flatMap((quote) => [
      ...subRecordLine({
        key: HotelsAttachField.Guest,
        parts: [{ key: HotelsAttachPart.Name, value: quote.name }],
      }),
      ...textLine({ key: HotelsAttachField.GuestSaid, value: quote.text }),
    ]),
    ...result.otherSiteRatings.flatMap((site) =>
      subRecordLine({
        key: HotelsAttachField.OtherSite,
        parts: [
          { key: HotelsAttachPart.Site, value: site.site },
          { key: HotelsAttachPart.Rating, value: numberPart({ value: site.rating }) },
          { key: HotelsAttachPart.Reviews, value: numberPart({ value: site.reviewCount }) },
        ],
      })),
    ...textLine({ key: HotelsAttachField.Photo, value: result.photoUrl }),
    ...textLine({ key: HotelsAttachField.PropertyPage, value: result.propertyPageUrl }),
  ];
  return { headline: hotelsAttachHeadline({ result }), fields };
}

// ─── reading ────────────────────────────────────────────────────────────────

function fieldText(input: { item: AttachItemBlock; key: HotelsAttachField }): string | null {
  return attachTextOf({ value: attachFieldValue({ item: input.item, key: input.key }) });
}

function fieldNumber(input: { item: AttachItemBlock; key: HotelsAttachField }): number | null {
  return attachNumberOf({ value: fieldText(input) });
}

function nearbyOf(input: { item: AttachItemBlock }): HotelsAttachNearby[] {
  return attachFieldValues({ item: input.item, key: HotelsAttachField.Nearby })
    .flatMap((value) => {
      const record = attachSubRecordOf({ value });
      const place = attachPartText({ record, key: HotelsAttachPart.Place });
      if (place === null) return [];
      return [
        {
          place,
          mode: attachPartText({ record, key: HotelsAttachPart.Mode }),
          duration: attachPartText({ record, key: HotelsAttachPart.Duration }),
        },
      ];
    });
}

function reviewAspectsOf(input: { item: AttachItemBlock }): HotelsAttachReviewAspect[] {
  return attachFieldValues({ item: input.item, key: HotelsAttachField.ReviewAspect })
    .flatMap((value) => {
      const record = attachSubRecordOf({ value });
      const aspect = attachPartText({ record, key: HotelsAttachPart.Aspect });
      if (aspect === null) return [];
      return [
        {
          aspect,
          total: attachPartNumber({ record, key: HotelsAttachPart.Total }),
          positive: attachPartNumber({ record, key: HotelsAttachPart.Positive }),
          negative: attachPartNumber({ record, key: HotelsAttachPart.Negative }),
        },
      ];
    });
}

function bestOfferOf(input: { item: AttachItemBlock }): HotelsAttachBestOffer | null {
  const value = attachFieldValue({ item: input.item, key: HotelsAttachField.BestOffer });
  if (value === null) return null;
  const record = attachSubRecordOf({ value });
  const seller = attachPartText({ record, key: HotelsAttachPart.Seller });
  if (seller === null) return null;
  return {
    seller,
    pricePerNightPretaxUsd: attachPartNumber({
      record,
      key: HotelsAttachPart.PricePerNightPretax,
    }),
    freeCancellation: attachPartFlag({ record, key: HotelsAttachPart.FreeCancellation }),
  };
}

/** Null when the block stated no verdict at all — the four members arrive as a
 *  unit or not at all, and an object of four nulls would claim the vendor was
 *  asked and said nothing. */
function priceCheckOf(input: { item: AttachItemBlock }): HotelsAttachPriceCheck | null {
  const check: HotelsAttachPriceCheck = {
    lowestText: fieldText({ item: input.item, key: HotelsAttachField.PriceLowest }),
    level: fieldText({ item: input.item, key: HotelsAttachField.PriceLevel }),
    typicalLowText: fieldText({ item: input.item, key: HotelsAttachField.PriceTypicalLow }),
    typicalHighText: fieldText({ item: input.item, key: HotelsAttachField.PriceTypicalHigh }),
  };
  const stated =
    check.lowestText !== null ||
    check.level !== null ||
    check.typicalLowText !== null ||
    check.typicalHighText !== null;
  return stated ? check : null;
}

function offersOf(input: { item: AttachItemBlock }): HotelsAttachOffer[] {
  return attachFieldGroups({
    item: input.item,
    openKey: HotelsAttachField.Offer,
    memberKeys: [HotelsAttachField.OfferRoom, HotelsAttachField.Book],
  }).map((group) => {
    const record = attachSubRecordOf({ value: group.open });
    return {
      seller: attachPartText({ record, key: HotelsAttachPart.Seller }),
      pricePerNightUsd: attachPartNumber({ record, key: HotelsAttachPart.PricePerNight }),
      pricePerNightPretaxUsd: attachPartNumber({
        record,
        key: HotelsAttachPart.PricePerNightPretax,
      }),
      totalStayUsd: attachPartNumber({ record, key: HotelsAttachPart.TotalStay }),
      freeCancellation: attachPartFlag({ record, key: HotelsAttachPart.FreeCancellation }),
      freeCancellationUntilDate: attachPartText({ record, key: HotelsAttachPart.CancelByDate }),
      freeCancellationUntilTime: attachPartText({ record, key: HotelsAttachPart.CancelByTime }),
      rooms: attachGroupValues({ group, key: HotelsAttachField.OfferRoom }),
      bookUrl: attachGroupValue({ group, key: HotelsAttachField.Book }),
    };
  });
}

function guestQuotesOf(input: { item: AttachItemBlock }): HotelsAttachGuestQuote[] {
  return attachFieldGroups({
    item: input.item,
    openKey: HotelsAttachField.Guest,
    memberKeys: [HotelsAttachField.GuestSaid],
  }).map((group) => ({
    name: attachPartText({
      record: attachSubRecordOf({ value: group.open }),
      key: HotelsAttachPart.Name,
    }),
    text: attachGroupValue({ group, key: HotelsAttachField.GuestSaid }),
  }));
}

function otherSitesOf(input: { item: AttachItemBlock }): HotelsAttachOtherSiteRating[] {
  return attachFieldValues({ item: input.item, key: HotelsAttachField.OtherSite })
    .flatMap((value) => {
      const record = attachSubRecordOf({ value });
      const site = attachPartText({ record, key: HotelsAttachPart.Site });
      if (site === null) return [];
      return [
        {
          site,
          rating: attachPartNumber({ record, key: HotelsAttachPart.Rating }),
          reviewCount: attachPartNumber({ record, key: HotelsAttachPart.Reviews }),
        },
      ];
    });
}

function propertyKindOf(input: { item: AttachItemBlock }): HotelsAttachPropertyKind | null {
  const value = fieldText({ item: input.item, key: HotelsAttachField.PropertyKind });
  if (value === null) return null;
  return HOTELS_PROPERTY_KINDS.has(value) ? (value as HotelsAttachPropertyKind) : null;
}

/**
 * One block item, read as a typed property — or null when the item carries no
 * name, which is the one field a property cannot be without.
 *
 * THE HEADLINE IS NOT READ. Every value comes off a field line, so the typed
 * object is a function of the addressable data alone.
 */
export function readHotelsAttachItem(input: {
  item: AttachItemBlock;
}): HotelsAttachResult | null {
  const item = input.item;
  const name = fieldText({ item, key: HotelsAttachField.Name });
  if (name === null) return null;
  return {
    name,
    propertyKind: propertyKindOf({ item }),
    stars: fieldNumber({ item, key: HotelsAttachField.Stars }),
    rating: fieldNumber({ item, key: HotelsAttachField.Rating }),
    reviewCount: fieldNumber({ item, key: HotelsAttachField.Reviews }),
    nights: fieldNumber({ item, key: HotelsAttachField.Nights }),
    currency: fieldText({ item, key: HotelsAttachField.Currency }),
    pricePerNightUsd: fieldNumber({ item, key: HotelsAttachField.PricePerNight }),
    pricePerNightPretaxUsd: fieldNumber({ item, key: HotelsAttachField.PricePerNightPretax }),
    totalStayUsd: fieldNumber({ item, key: HotelsAttachField.TotalStay }),
    city: fieldText({ item, key: HotelsAttachField.City }),
    country: fieldText({ item, key: HotelsAttachField.Country }),
    coordinates: attachCoordinatesOf({
      value: fieldText({ item, key: HotelsAttachField.Coordinates }),
    }),
    address: fieldText({ item, key: HotelsAttachField.Address }),
    phone: fieldText({ item, key: HotelsAttachField.Phone }),
    locationRating: fieldNumber({ item, key: HotelsAttachField.LocationRating }),
    thingsToDoRating: fieldNumber({ item, key: HotelsAttachField.ThingsToDoRating }),
    transitRating: fieldNumber({ item, key: HotelsAttachField.TransitRating }),
    airportRating: fieldNumber({ item, key: HotelsAttachField.AirportRating }),
    nearby: nearbyOf({ item }),
    checkInTime: fieldText({ item, key: HotelsAttachField.CheckIn }),
    checkOutTime: fieldText({ item, key: HotelsAttachField.CheckOut }),
    amenities: attachFieldValues({ item, key: HotelsAttachField.Amenity }),
    essentials: attachFieldValues({ item, key: HotelsAttachField.Essential }),
    reviewAspects: reviewAspectsOf({ item }),
    bestOffer: bestOfferOf({ item }),
    priceCheck: priceCheckOf({ item }),
    offers: offersOf({ item }),
    guestQuotes: guestQuotesOf({ item }),
    otherSiteRatings: otherSitesOf({ item }),
    photoUrl: fieldText({ item, key: HotelsAttachField.Photo }),
    propertyPageUrl: fieldText({ item, key: HotelsAttachField.PropertyPage }),
  };
}

